<script setup lang="ts">
/**
 * WhatIsYfb — 「什么是隐分宝」section
 * Hero 之后，谁需要隐分宝之前
 * 3 痛点 + 1 句插件介绍，黑底卡片堆叠
 */
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import YfbIcon from '@/components/YfbIcon.vue'
import type { IconName } from '@/types/icons'

interface PainPoint {
  icon: IconName
  title: string
  desc: string
}

const { tm } = useI18n()

const painPoints = computed<PainPoint[]>(() => {
  const ref = tm('home.whatIsYfb.pains') as any
  return ref?.value ?? ref ?? []
})
</script>

<template>
  <section id="what-is-yfb" class="what">
    <div class="what__inner">
      <header class="what__header">
        <span class="what__eyebrow">{{ tm('home.whatIsYfb.eyebrow') }}</span>
        <h2 class="what__title">{{ tm('home.whatIsYfb.title') }}</h2>
        <p class="what__lead">{{ tm('home.whatIsYfb.lead') }}</p>
      </header>

      <ol class="what__pains">
        <li
          v-for="(p, idx) in painPoints"
          :key="idx"
          class="what__pain"
        >
          <span class="what__pain-num">0{{ idx + 1 }}</span>
          <span class="what__pain-icon">
            <YfbIcon :name="p.icon" :size="20" />
          </span>
          <div class="what__pain-body">
            <h3 class="what__pain-title">{{ p.title }}</h3>
            <p class="what__pain-desc">{{ p.desc }}</p>
          </div>
        </li>
      </ol>

      <div class="what__intro">
        <span class="what__intro-tag">{{ tm('home.whatIsYfb.introTag') }}</span>
        <p class="what__intro-text">{{ tm('home.whatIsYfb.intro') }}</p>
      </div>
    </div>
  </section>
</template>

<style scoped>
.what {
  padding: var(--yfb-sp-9) var(--yfb-sp-6);
  border-bottom: 1px solid var(--yfb-rule-soft);
}

.what__inner {
  max-width: 1200px;
  margin: 0 auto;
}

.what__header {
  text-align: center;
  margin-bottom: 40px;
}

.what__eyebrow {
  display: inline-block;
  padding: var(--yfb-sp-1) var(--yfb-sp-3);
  background-color: var(--yfb-surface);
  color: var(--yfb-accent);
  border: 1px solid var(--yfb-rule);
  border-radius: var(--yfb-radius-sm);
  font-family: var(--yfb-font-heading);
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: var(--yfb-ls-eyebrow);
  margin-bottom: 16px;
  text-shadow: 0 0 10px var(--yfb-glow);
}

.what__title {
  font-family: var(--yfb-font-heading);
  font-size: var(--yfb-fs-3xl);
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: var(--yfb-ls-display);
  margin: 0 0 16px;
}

.what__lead {
  font-size: var(--yfb-fs-lg);
  color: var(--yfb-text-secondary);
  margin: 0 auto;
  max-width: 720px;
  line-height: var(--yfb-lh-lead);
}

/* 痛点列表 — 深色玻璃卡片堆叠 */
.what__pains {
  list-style: none;
  padding: 0;
  margin: 0 0 32px;
  display: flex;
  flex-direction: column;
  gap: var(--yfb-sp-3);
  background-color: transparent;
  color: var(--yfb-ink);
}

.what__pain {
  display: flex;
  align-items: stretch;
  gap: 0;
  background-color: var(--yfb-surface);
  border: 1px solid var(--yfb-rule);
  border-radius: var(--yfb-radius-lg);
  overflow: hidden;
  transition: border-color var(--yfb-anim-fast) ease-out, box-shadow var(--yfb-anim-fast) ease-out;
}

.what__pain:hover {
  border-color: var(--yfb-accent);
  box-shadow: 0 0 14px var(--yfb-glow);
}

.what__pain-num {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 72px;
  padding: var(--yfb-sp-6) var(--yfb-sp-4);
  background-color: var(--yfb-accent);
  color: var(--yfb-on-accent);
  font-family: var(--yfb-font-heading);
  font-size: var(--yfb-fs-2xl);
  font-weight: 700;
  letter-spacing: var(--yfb-ls-mono);
}

.what__pain-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 56px;
  padding: var(--yfb-sp-6) var(--yfb-sp-4);
  background-color: var(--yfb-surface-2);
  color: var(--yfb-accent);
  border-right: 1px solid var(--yfb-rule-soft);
}

.what__pain-body {
  flex: 1;
  padding: var(--yfb-sp-5) var(--yfb-sp-6);
  display: flex;
  flex-direction: column;
  gap: var(--yfb-sp-1);
  justify-content: center;
}

.what__pain-title {
  font-size: var(--yfb-fs-lg);
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: var(--yfb-ls-button);
  margin: 0;
  color: var(--yfb-ink);
}

.what__pain-desc {
  font-size: var(--yfb-fs-base);
  font-weight: 400;
  line-height: var(--yfb-lh-lead);
  margin: 0;
  color: var(--yfb-text-secondary);
}

/* 一句插件介绍 — 渐变 CTA 横条 */
.what__intro {
  display: flex;
  align-items: stretch;
  gap: 0;
  background-image: var(--yfb-grad-cta);
  color: var(--yfb-on-accent);
  border-radius: var(--yfb-radius-lg);
  box-shadow: 0 0 18px var(--yfb-glow);
  overflow: hidden;
}

.what__intro-tag {
  display: flex;
  align-items: center;
  padding: var(--yfb-sp-4) var(--yfb-sp-5);
  background-color: rgba(11, 11, 16, 0.2);
  color: var(--yfb-on-accent);
  font-family: var(--yfb-font-heading);
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: var(--yfb-ls-eyebrow);
  border-right: 1px solid rgba(10, 10, 18, 0.25);
  white-space: nowrap;
}

.what__intro-text {
  flex: 1;
  padding: var(--yfb-sp-4) var(--yfb-sp-6);
  font-size: var(--yfb-fs-base);
  font-weight: 600;
  line-height: var(--yfb-lh-lead);
  margin: 0;
  display: flex;
  align-items: center;
}

@media (max-width: 767px) {
  .what {
    padding: var(--yfb-sp-8) var(--yfb-sp-4);
  }

  .what__pain-num {
    min-width: 56px;
    padding: var(--yfb-sp-4) var(--yfb-sp-2);
    font-size: var(--yfb-fs-xl);
  }

  .what__pain-icon {
    min-width: 44px;
    padding: var(--yfb-sp-4) var(--yfb-sp-2);
  }

  .what__pain-body {
    padding: var(--yfb-sp-4);
  }

  .what__intro {
    flex-direction: column;
  }

  .what__intro-tag {
    border-right: none;
    border-bottom: 1px solid rgba(10, 10, 18, 0.25);
    padding: var(--yfb-sp-3) var(--yfb-sp-4);
  }
}
</style>
